import { CohortEnum } from '@eigtech/auth0-types'
import { formatStringAsDate, STANDARD_DATE_TIME_FORMAT } from '@eigtech/ui-shared-dates'
import {
  Button,
  ComposedCard,
  Heading,
  HStack,
  Page,
  Text,
  Tooltip,
  useCopyToClipboard,
} from '@eigtech/ui-shared-dave'
import { MdContentCopy, MdShare, MdWarning } from 'react-icons/md'
import { useGetMeeting } from '../../api/getMeeting'
import { useGetMeetingVideo } from '../../api/getMeetingVideo'
import { useGetMeetingVideoShareLink } from '../../api/getMeetingVideoShareLink'
import { MeetingVideo, MeetingVideoProps } from './MeetingVideo'
import { ScreenshotTable } from './ScreenshotTable'
import { MeetingInfo, MeetingUploadRoute } from './types'

export type MeetingDetailProps = {
  cohort: CohortEnum
  meetingDescription?: string
  meetingUploadRoute?: MeetingUploadRoute
} & MeetingInfo &
  Pick<MeetingVideoProps, 'getMetaForScreenshotUpload'>

export function MeetingDetail({
  cohort,
  entityId,
  getMetaForScreenshotUpload,
  meetingDescription,
  meetingId,
  meetingUploadRoute,
  startTime,
}: MeetingDetailProps) {
  const { data: meeting } = useGetMeeting({
    entityId,
    meetingId,
    startTime,
  })

  return (
    <Page
      belowHeader={
        (!!meetingDescription && <Text>{meetingDescription}</Text>) || <Text>No Description</Text>
      }
      heading={
        <Heading as="h2" size="md">
          {formatStringAsDate(startTime, STANDARD_DATE_TIME_FORMAT)}{' '}
          {meeting?.endTime
            ? `- ${formatStringAsDate(meeting.endTime, STANDARD_DATE_TIME_FORMAT)}`
            : ''}
        </Heading>
      }
    >
      <ComposedCard
        heading={
          <HStack>
            <Heading as="h2" flex="1" size="md">
              Inspection Video
            </Heading>

            <ShareMeetingVideo entityId={entityId} meetingId={meetingId} startTime={startTime} />
          </HStack>
        }
      >
        <MeetingVideo
          cohort={cohort}
          entityId={entityId}
          getMetaForScreenshotUpload={getMetaForScreenshotUpload}
          meetingId={meetingId}
          startTime={startTime}
        />
      </ComposedCard>
      <ScreenshotTable
        entityId={entityId}
        meetingId={meetingId}
        meetingUploadRoute={meetingUploadRoute}
        startTime={startTime}
      />
    </Page>
  )
}

function ShareMeetingVideo(
  props: Pick<MeetingDetailProps, 'entityId' | 'meetingId' | 'startTime'>
) {
  const { data: video } = useGetMeetingVideo(props)

  const { copyToClipboard } = useCopyToClipboard()

  const { data, isFetching, isError, refetch } = useGetMeetingVideoShareLink(props, {
    enabled: false,
  })

  function copyShareLink(url: string | undefined = data?.shareLink) {
    if (!url) return

    copyToClipboard(url)
  }

  async function getShareLink() {
    const response = await refetch()

    if (response.data?.shareLink) {
      copyShareLink(response.data.shareLink)
    }
  }

  if (!video) {
    return <></>
  }

  return data?.shareLink ? (
    <Button rightIcon={<MdContentCopy />} onClick={() => copyShareLink()}>
      Copy Share Link
    </Button>
  ) : (
    <Tooltip isDisabled={!isError} label="Could not retrieve meeting video share link">
      <Button
        colorScheme={isError ? 'red' : undefined}
        isLoading={isFetching}
        rightIcon={isError ? <MdWarning /> : <MdShare />}
        onClick={getShareLink}
      >
        Get Share Link
      </Button>
    </Tooltip>
  )
}
